import { useMemo } from 'react';
import { useSchedulesPerfectSlots } from 'shared/hooks/use-schedules-perfect-slots';
import { useTimeframeCompletions } from 'shared/hooks/use-timeframe-completions';
import { useTimeframeEntries } from 'shared/hooks/use-timeframe-entries';
import { useTimeframeOccurrences } from 'shared/hooks/use-timeframe-occurrences';
import { useTimeframeSchedules } from 'shared/hooks/use-timeframe-schedules';
import { HabitSchedule, ScheduleEntryState } from 'shared/types/habit-schedule';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { isNumber } from 'shared/utils/is-number';

export type Options = {
  startDate: Timestamp;
  endDate: Timestamp;
  weekStartsOn: WeekDays;
  today: Timestamp;
};

export const useWeekStatistics = (
  schedules: HabitSchedule[],
  { startDate, endDate, weekStartsOn, today }: Options,
) => {
  const timeframeSchedules = useTimeframeSchedules(schedules, {
    startDate,
    endDate,
  });
  const { timeframeOccurrences } = useTimeframeOccurrences(schedules, {
    startDate,
    endDate,
  });
  const { dateEntries, timeframeDateEntries } = useTimeframeEntries(schedules, {
    startDate,
    endDate,
  });

  const { timeframeCompletions } = useTimeframeCompletions(schedules, {
    startDate,
    endDate,
  });
  const [perfectWeek] = useSchedulesPerfectSlots(schedules, {
    startDate,
    endDate,
    weekStartsOn,
    strictDates: true,
    today,
  });

  const { completionsCount, target } = useMemo(() => {
    const lastSchedule = timeframeSchedules[timeframeSchedules.length - 1];
    if (!lastSchedule) {
      return {};
    }

    if (lastSchedule.frequency) {
      const completionsCount = timeframeCompletions.reduce(
        (acc, { state, value }) =>
          acc + (state === ScheduleEntryState.Complete && !!value ? value : 0),
        0,
      );
      const target = lastSchedule.frequency?.count ?? 1;

      return { completionsCount, target };
    }

    return {
      completionsCount: timeframeCompletions.reduce(
        (acc, { value }) => acc + (value ?? 0),
        0,
      ),
      target: timeframeDateEntries.reduce(
        (acc, entry) => acc + (entry.target ?? 0),
        0,
      ),
    };
  }, [timeframeCompletions, timeframeDateEntries, timeframeSchedules]);

  const completionPercentage =
    isNumber(completionsCount) && isNumber(target)
      ? Math.round((completionsCount! / Math.max(1, target!)) * 1000) / 10
      : undefined;

  return {
    occurrences: timeframeOccurrences,
    dateEntries,
    perfectWeek,
    completionsCount,
    target,
    completionPercentage:
      completionPercentage && completionPercentage >= 10
        ? Math.round(completionPercentage)
        : completionPercentage,
  };
};
